import { BetslipAccordionHeaderClick, UI__BETSLIP_ACCORDION_HEADER_CLICK } from "@ppb/tbd-store/actions/betslip";
import { getSportsbookBettingLegs } from "@ppb/tbd-store/state/betting/sportsbook-betting/sportsbook-betting-selectors";
import { getBettingResolvers } from "../../../store/state/sportsbook-betting/betting-resolver";
import {
  getSportsbookBettingImplyRunnerFailures,
  createGetSelectionIdsSelector,
} from "../../../store/state/sportsbook-betting/sportsbook-betting-selectors";
import type { BettingMetadata } from "@ppb/tbd-store/state/betting/sportsbook-betting/SportsbookBetting.types";
import { BetslipApplicationState } from "../../../store/state/BetslipApplicationState";
import { LEG_TYPES } from "@ppb/betslip-core";
import { MapDispatchToProps, MapStateToPropsFactory } from "react-redux";
import { Dispatch } from "redux";
import { i18n } from "../../../helpers/i18n";
import {
  createGetConfirmationSelectionIdsSelector,
  createIsConfirmStep,
} from "../../sportsbook-betslip-confirm-mapper";
import { ContainerProps, DispatchProps, StateProps } from "../props";

import {
  getBetslipGroup,
  getSportsbookConfirmationLegs,
} from "../../../store/state/betslip-module/betslip-module-selectors";
import { buildHintGroups, LegHintContext } from "../../../helpers/hint-groups";
import { LegHintKind, getLegHint } from "../../../helpers/leg-hint";

const getLegHintScope = (metadata: BettingMetadata | undefined): string | undefined => {
  if (!metadata) return undefined;

  return metadata.type === "RACING" ? metadata.racing.urn : metadata.eventUrn;
};

export const makeMapStateToProps: MapStateToPropsFactory<StateProps, ContainerProps, BetslipApplicationState> = () => {
  const getIsConfirmStep = createIsConfirmStep();
  const getConfirmationSelectionIds = createGetConfirmationSelectionIdsSelector();
  const getSelectionIds = createGetSelectionIdsSelector();

  return (appState: BetslipApplicationState, { legIds, capabilities }: ContainerProps) => {
    const isConfirmStep = getIsConfirmStep(appState);
    const hasSelectionConnector = capabilities.selectionConnector.enabled;

    const allLegIds = isConfirmStep
      ? getConfirmationSelectionIds(appState)
      : getSelectionIds(appState.betting.sportsbookBetting.legs);

    const finalLegIds = legIds?.length ? legIds : allLegIds.filter((legid) => !legid.includes(LEG_TYPES.ONE_LINE_BET));

    const legs = isConfirmStep ? getSportsbookConfirmationLegs(appState) : getSportsbookBettingLegs(appState);
    const failedRunners = getSportsbookBettingImplyRunnerFailures(appState);
    const group = getBetslipGroup(appState);
    const resolvers = getBettingResolvers(group);
    const runnersMetadata = resolvers.getMetadata(appState);

    const buildLegHintContext = (legId: string): LegHintContext => {
      const legRunnerId = legs[legId]?.runners?.[0];
      const runnerFailures = (legRunnerId && failedRunners[legRunnerId]) || [];
      const hint = getLegHint(runnerFailures);
      const scope =
        hint?.kind === LegHintKind.NotCombinable && legRunnerId
          ? getLegHintScope(runnersMetadata[legRunnerId])
          : undefined;

      return { legId, hint, scope };
    };

    const contexts = finalLegIds.map(buildLegHintContext);

    return {
      title: i18n({
        key: "I18N.BETSLIP.SELECTIONS_COUNT",
        interpolationValues: { numberOfSelections: `${finalLegIds.length}` },
      }),
      legIds: finalLegIds,
      legHints: buildHintGroups(contexts),
      hasSelectionConnector,
    };
  };
};

export const mapDispatchToProps: MapDispatchToProps<DispatchProps, ContainerProps> = (dispatch: Dispatch) => ({
  dispatchCollapseToggle: (isExpanded: boolean) => {
    dispatch<BetslipAccordionHeaderClick>({
      type: UI__BETSLIP_ACCORDION_HEADER_CLICK,
      payload: { isExpanded },
    });
  },
});
